import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { i18n } from '../src/i18n/config';
// 기존 애플리케이션 컴포넌트들 가져오기
import { MainLayoutWithChat } from '../src/layouts/MainLayoutWithChat';
import RootLayout from "../src/layouts/RootLayout";
import LandingPageWithSharedLogin from "../src/components/pages/LandingPageWithSharedLogin";
import { PricingPage } from '../components/pages/PricingPage';
import { ErrorBoundary } from '@edusense/shared-ui-components';
import { isLearnSurface } from '@edusense/shared-constants';
// OAuth2 리다이렉트 핸들러 (특수 라우트)
import OAuth2RedirectHandler from '../pages/OAuth2RedirectHandler';
// 새로운 설정 기반 라우팅 시스템 - 공유 라이브러리 사용
import { AutoI18nLayout } from '@edusense/shared-routing';
import { LanguageService } from '@edusense/shared-i18n';
// 로컬 설정을 공유 LanguageService에 적용
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, BROWSER_LANGUAGE_MAP, LANGUAGE_DETECTION_CONFIG } from '../config/routing.config';
// React Router v7을 위한 명시적 페이지 import
import MainPage from '../components/pages/main/MainPage/MainPage';
import DiscoverPageComponent from '../components/pages/discovery/DiscoverPage/DiscoverPage';
import CoursesPage from '../components/pages/CoursesPage';
import ProfilePage from '../components/pages/user/ProfilePage';
import { ProfileEditPage } from '@edusense/user-profile';
import SettingsPageWrapper from '../components/pages/system/SettingsPage/SettingsPageWrapper';
import ProjectDetailPage from '../components/pages/project/ProjectDetailPage/ProjectDetailPage';
import ProjectUploadPage from '../components/pages/project/ProjectUploadPage/ProjectUploadPage';
import RoadmapFormPage from '../components/pages/RoadmapCreatePage';
import RoadmapDetailPage from '../components/pages/RoadmapDetailPage';
import ActivityPage from '../components/pages/post/ActivityPage/ActivityPage';
import CourseEditPage from '@edusense/pages-course-edit';
import EditorUsageGuidePage from '../components/pages/EditorUsageGuidePage';
import FollowsPage from '../components/pages/user/FollowsPage/FollowsPage';
import NotificationsPage from '../components/pages/communication/NotificationsPage/NotificationsPage';
import CourseDetailPage from '../components/pages/CourseDetailPage';
import CourseReviewsPage from '../components/pages/CourseReviewsPage';
import LecturePage from '../components/pages/LecturePage';
import LecturePreviewPage from '../components/pages/LecturePreviewPage';
import PostPage from '../components/pages/post/PostPage/PostPage';

// 🚀 2️⃣ LIBRARY BOUNDARY FIX: shared-ui-layout이 직접 Outlet 렌더링
const MainLayoutWithChatWrapper = () => {
  return <MainLayoutWithChat />; // children 제거 - shared-ui-layout이 직접 <Outlet /> 처리
};

// 🚀 Shared LanguageService 설정 (edusense-frontend 로컬 설정 적용)
LanguageService.configure({
  supportedLanguages: SUPPORTED_LANGUAGES,
  defaultLanguage: DEFAULT_LANGUAGE,
  browserLanguageMap: BROWSER_LANGUAGE_MAP,
  detectionConfig: LANGUAGE_DETECTION_CONFIG,
});

// 🚀 React Router v7: createBrowserRouter로 location context 문제 해결
// learn.ugot.uk shows courses only: the catalog, course pages, lectures and
// the creator's profile. Every other path lands on the catalog.
const LEARN = isLearnSurface();
const LEARN_PATHS = /^(learn|courses|course|profile)(\/|$)/;
const home = LEARN ? 'learn' : 'feed';
// learn.ugot.uk has no feed, pricing or social pages; anything else under a
// language lands on the course catalog in that language. The target is absolute:
// a relative one resolves against the unmatched path (/ko/feed → /ko/feed/learn).
const LEARN_LANGS = /^(ko|en|ja)$/;
function ToLearnCatalog() {
  const { lang } = useParams();
  return <Navigate to={`/${lang && LEARN_LANGS.test(lang) ? lang : 'ko'}/learn`} replace />;
}
const learnOnly = <T extends { path?: string }>(routes: T[]): T[] =>
  LEARN ? routes.filter(route => route.path !== undefined && LEARN_PATHS.test(route.path)) : routes;

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary><div>라우트 오류가 발생했습니다.</div></ErrorBoundary>,
    children: [
      {
        path: 'oauth2/redirect',
        element: <OAuth2RedirectHandler />
      },
      {
        path: ':lang?',
        element: <AutoI18nLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={home} replace />
          },
          {
            path: 'login',
            element: <LandingPageWithSharedLogin />
          },
          ...(LEARN ? [] : [{
            path: 'pricing',
            element: <PricingPage />
          }]),
          {
            path: 'landing',
            element: LEARN ? <ToLearnCatalog /> : <LandingPageWithSharedLogin />
          },
          {
            path: '',
            element: <MainLayoutWithChatWrapper />,
            children: learnOnly([
              { path: 'feed', element: <MainPage /> },
              { path: 'discover', element: <DiscoverPageComponent /> },
              { path: 'learn', element: <CoursesPage /> },
              { path: 'courses', element: <CoursesPage /> },
              { path: 'learn/course/new/edit', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId/edit', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId/curriculum', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId/pricing', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId/resources', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId/settings', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId/publish', element: <CourseEditPage /> },
              { path: 'learn/course/:courseId', element: <CourseDetailPage /> },
              { path: 'learn/course/:courseId/reviews', element: <CourseReviewsPage /> },
              { path: 'learn/course/:courseId/lecture/:lectureId', element: <LecturePage /> },
              { path: 'learn/course/:courseId/preview/:sectionIndex/:lectureIndex', element: <LecturePreviewPage /> },
              { path: 'learn/roadmap/new', element: <RoadmapFormPage /> },
              { path: 'learn/roadmap/:roadmapId/edit', element: <RoadmapFormPage /> },
              { path: 'learn/roadmap/:roadmapId', element: <RoadmapDetailPage /> },
              { path: 'course/:courseId', element: <CourseDetailPage /> },
              { path: 'learn/editor-guides', element: <EditorUsageGuidePage /> },
              { path: 'learn/editor-guides/:section', element: <EditorUsageGuidePage /> },
              { path: 'activity', element: <ActivityPage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'profile/:username/edit', element: <ProfileEditPage /> },
              { path: 'profile/:username', element: <ProfilePage /> },
              { path: 'profile/:username/followers', element: <FollowsPage initialType="followers" /> },
              { path: 'profile/:username/following', element: <FollowsPage initialType="following" /> },
              { path: 'user/edit', element: <ProfileEditPage /> },
              { path: 'settings', element: <SettingsPageWrapper /> },
              { path: 'project/:projectId', element: <ProjectDetailPage /> },
              { path: 'project/new', element: <ProjectUploadPage /> },
              { path: 'post/:postId', element: <PostPage /> }
            ])
          },
          ...(LEARN ? [{ path: '*', element: <ToLearnCatalog /> }] : [])
        ]
      },
      {
        path: '*',
        element: <Navigate to={`/ko/${home}`} replace />
      }
    ]
  }
]);

export default function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
